import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "../common/Container";
import SectionTitle from "../common/SectionTitle";
import { projects } from "../../data/siteData";
import {
  FaXmark,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarDays,
  FaLocationDot,
} from "react-icons/fa6";
import "../../styles/Projects.css";

// Fisher-Yates shuffle
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Projects() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // ✅ Random 9 images for homepage (changes on refresh)
  const previewCount = 9;
  const previewProjects = useMemo(() => shuffle(projects).slice(0, previewCount), []);

  const currentProject = projects[activeIndex];

  // ===== Modal background blur (no flash) =====
  const [bgA, setBgA] = useState("");
  const [bgB, setBgB] = useState("");
  const [showA, setShowA] = useState(true);

  // When modal opens first time
  useEffect(() => {
    if (!isModalOpen || !currentProject) return;
    setBgA(currentProject.src);
    setBgB(currentProject.src);
    setShowA(true);
  }, [isModalOpen, currentProject]);

  // Cross-fade background when active image changes (no disappearing)
  useEffect(() => {
    if (!isModalOpen || !currentProject) return;

    // Load next bg into the hidden layer and then fade
    if (showA) {
      setBgB(currentProject.src);
      requestAnimationFrame(() => setShowA(false));
    } else {
      setBgA(currentProject.src);
      requestAnimationFrame(() => setShowA(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  const openModalAt = (index) => {
    setActiveIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
  };

  const next = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev + 1) % projects.length);
  };

  const prev = (e) => {
    e?.stopPropagation();
    setActiveIndex((prev) => (prev - 1 + projects.length) % projects.length);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isModalOpen]);

  // Swipe (mobile)
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const dx = touchStartX.current - touchEndX.current;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) setActiveIndex((p) => (p + 1) % projects.length);
    else setActiveIndex((p) => (p - 1 + projects.length) % projects.length);
  };

  return (
    <section id="projects" className="projects-section">
      <Container>
        <SectionTitle
          badge="Our Portfolio"
          title="Exceptional Spaces, Expertly Cleaned"
          desc="We take pride in every corner we touch. Here is a curated selection of our recent transformations."
        />

        <div className="projects-grid-pro">
          {previewProjects.map((project) => {
            // Map preview tile to real index in full gallery
            const fullIndex = projects.findIndex((p) => p.src === project.src);

            return (
              <button
                key={project.src}
                className="project-tile"
                type="button"
                onClick={() => openModalAt(fullIndex)}
              >
                <img
                  src={project.src}
                  alt={project.title}
                  className="project-tile-img"
                  loading="lazy"
                />
                <div className="project-tile-overlay">
                  <div className="project-tile-text">
                    <span>{project.category || "Service"}</span>
                    <h3>{project.title}</h3>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="projects-viewmore-wrap">
          <button className="projects-viewmore-btn-pro" onClick={() => openModalAt(0)}>
            View All Projects
          </button>
        </div>
      </Container>

      {/* PREMIUM LIGHTBOX */}
      {isModalOpen && currentProject && (
        <div className="lb-overlay" onClick={closeModal}>
          {/* ✅ Crossfade blur layers (no flash) */}
          <div className="lb-bg-stack" aria-hidden="true">
            <div
              className={`lb-bg-blur-layer ${showA ? "is-visible" : ""}`}
              style={{ backgroundImage: `url(${bgA})` }}
            />
            <div
              className={`lb-bg-blur-layer ${!showA ? "is-visible" : ""}`}
              style={{ backgroundImage: `url(${bgB})` }}
            />
          </div>

          <div
            className="lb-container"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <button className="lb-close-btn" onClick={closeModal} aria-label="Close">
              <FaXmark />
            </button>

            <div className="lb-content-wrapper">
              {/* Left: Image Pane */}
              <div className="lb-image-pane">
                <button className="lb-nav-btn lb-prev" onClick={prev} aria-label="Previous">
                  <FaChevronLeft />
                </button>

                {/* Optional preloading helpers */}
                <img
                  src={currentProject.src}
                  alt={currentProject.title}
                  className="lb-main-img"
                />

                <button className="lb-nav-btn lb-next" onClick={next} aria-label="Next">
                  <FaChevronRight />
                </button>
              </div>

              {/* Right: Info Pane */}
              <div className="lb-info-pane">
                <div className="lb-info-header">
                  <span className="lb-tag">{currentProject.category || "Professional Cleaning"}</span>
                  <h2 className="lb-title">{currentProject.title}</h2>
                  <div className="lb-divider"></div>
                </div>

                <div className="lb-details">
                  <p className="lb-description">
                    {currentProject.description ||
                      "High-standard professional cleaning service delivered with precision and eco-friendly products to ensure a healthy environment."}
                  </p>

                  <div className="lb-stats-grid">
                    <div className="lb-stat-item">
                      <FaCalendarDays className="lb-icon" />
                      <div>
                        <label>Completed</label>
                        <p>Jan 2024</p>
                      </div>
                    </div>
                    <div className="lb-stat-item">
                      <FaLocationDot className="lb-icon" />
                      <div>
                        <label>Location</label>
                        <p>Metro Area</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lb-footer">
                  <button className="lb-action-btn" onClick={closeModal}>
                    Back to Gallery
                  </button>
                  <span className="lb-counter">
                    {activeIndex + 1} / {projects.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
